import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { Button } from "../../../components/Button/Button";
import { WysiwygEditor } from "../../../components/forms/WysiwygEditor/WysiwygEditor";
import { Collapse } from "../../../components/Collapse/Collapse";

interface DescriptionSectionProps {
  isLocked: boolean;
  isOpen: boolean;
  onToggle: () => void;
  description: string;
  handleDescriptionChange: (value: string) => void;
  triggerGuidelinesGeneration: () => Promise<void>;
  isGenerating: boolean;
  documentPath: string;
}

export const DescriptionSection: React.FC<DescriptionSectionProps> = ({
  isLocked,
  isOpen,
  onToggle,
  description,
  handleDescriptionChange,
  triggerGuidelinesGeneration,
  isGenerating,
  documentPath,
}) => {
  const { t } = useTranslation();

  return (
    <Collapse
      title={
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {isLocked && <span className="icon-[ph--lock] text-[#877369] text-base" />}
            <span
              className={classNames(
                "text-lg font-bold font-header",
                isLocked ? "text-[#877369]" : "text-[#6c2f00]",
              )}
            >
              {t("Pages:StyleCreator.Description.Title", "2. Public description")}
            </span>
          </div>
          {!isLocked && (
            <span className="text-xs text-[#54433a] font-normal font-sans">
              {t(
                "Pages:StyleCreator.Description.Subtitle",
                "This description is visible to participants and judges.",
              )}
            </span>
          )}
        </div>
      }
      isOpen={isOpen}
      onToggle={onToggle}
      variant="plain"
      density="comfortable"
      className={classNames(
        "rounded-xl border border-solid transition-all duration-200",
        isLocked ? "opacity-60 cursor-not-allowed" : "",
      )}
      headerClassName={isLocked ? "pointer-events-none" : ""}
    >
      <fieldset disabled={isLocked} className="space-y-6 w-full">
        <div className="flex justify-end items-start">
          <Button
            htmlType="button"
            type="primary"
            variant="filled"
            onClick={() => triggerGuidelinesGeneration()}
            disabled={isGenerating || (!documentPath && !description)}
            className="bg-[#f6e5db] hover:bg-[#f0dfd6] text-[#904d00] font-bold text-xs py-2 px-4 rounded-lg flex items-center gap-2 border border-[#dac2b6]/30"
          >
            {isGenerating ? (
              <>
                <span className="icon-[ph--spinner] text-sm animate-spin" />
                {t("Pages:StyleCreator.Description.Generating", "Generating...")}
              </>
            ) : (
              <>
                <span className="icon-[ph--sparkle] text-sm" />
                {t(
                  "Pages:StyleCreator.Description.Generate",
                  "Generate from source",
                )}
              </>
            )}
          </Button>
        </div>

        <div className="border border-[#dac2b6] rounded-xl overflow-hidden bg-white">
          <WysiwygEditor
            value={description}
            onChange={handleDescriptionChange}
            placeholder={t(
              "Pages:StyleCreator.Description.Placeholder",
              "Describe style characteristics...",
            )}
          />
          <div className="flex justify-end p-2 bg-[#fff8f5] text-[10px] text-[#877369] font-bold border-t border-[#dac2b6]">
            {description.length}{" "}
            {t("Pages:StyleCreator.Description.Characters", "CHARACTERS")}
          </div>
        </div>
      </fieldset>
    </Collapse>
  );
};
